import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { UploadSVG } from 'assets/img';
import { Button, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';

interface FileUploaderProps {
  isUploading?: boolean;
  onFileSelect: (file: File) => void;
  onFileRemove?: () => void;
  acceptedFileTypes?: string;
  maxFileSize?: number; // in bytes
  className?: string;
  showPreview?: boolean;
  maxPreviewRows?: number;
}

interface CSVPreviewData {
  headers: string[];
  rows: string[][];
  totalRows: number;
}

const FileUploaderContainer = styled.div<{ isDragging: boolean }>`
  border: 2px dashed ${({ isDragging }) => (isDragging ? '#4B68EF' : '#D4D5D6')};
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  background-color: ${({ isDragging }) => (isDragging ? '#F5F7FF' : '#FFFFFF')};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #4B68EF;
    background-color: #F5F7FF;
  }
`;

const UploadIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  
  svg {
    width: 48px;
    height: 48px;
  }
`;

const UploadText = styled.p`
  font-size: 16px;
  color: #163A50;
  margin: 0 0 8px 0;
  font-weight: 400;
  
  .link {
    color: #4B68EF;
    text-decoration: none;
    cursor: pointer;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const FileTypeText = styled.p`
  font-size: 14px;
  color: #9E9E9E;
  margin: 0;
  font-weight: 400;
`;

const HiddenInput = styled.input`
  display: none;
`;

const PreviewContainer = styled.div`
  margin-top: 24px;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  background-color: #FFFFFF;
  overflow: hidden;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background-color: #F5F5F5;
  border-bottom: 1px solid #E0E0E0;
`;

const PreviewHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FileIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #4B68EF;
  border-radius: 8px;
  
  svg {
    color: #FFFFFF;
    font-size: 24px;
  }
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const FileName = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #163A50;
  margin: 0;
`;

const FileSize = styled.p`
  font-size: 12px;
  color: #9E9E9E;
  margin: 0;
`;

const PreviewTableWrapper = styled.div`
  max-height: 400px;
  overflow: auto;
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  
  th {
    background-color: #F9FAFB;
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    color: #163A50;
    border-bottom: 2px solid #E0E0E0;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  
  td {
    padding: 12px 16px;
    border-bottom: 1px solid #F0F0F0;
    color: #424242;
  }
  
  tr:hover {
    background-color: #F9FAFB;
  }
`;

const PreviewFooter = styled.div`
  padding: 12px 16px;
  background-color: #F9FAFB;
  border-top: 1px solid #E0E0E0;
  text-align: center;
  font-size: 12px;
  color: #757575;
`;

const EmptyPreview = styled.div`
  padding: 40px;
  text-align: center;
  color: #9E9E9E;
  font-size: 14px;
`;

export const FileUploader: React.FC<FileUploaderProps> = ({
  isUploading = false,
  onFileSelect,
  onFileRemove,
  acceptedFileTypes = '.csv',
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  className,
  showPreview = true,
  maxPreviewRows = 10
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<CSVPreviewData | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const validateFile = (file: File): boolean => {
    // Check file type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const acceptedTypes = acceptedFileTypes.split(',').map(type => type.trim().toLowerCase());
    
    if (!acceptedTypes.includes(fileExtension)) {
      alert(`File type not supported. Please upload ${acceptedFileTypes} file.`);
      return false;
    }

    // Check file size
    if (file.size > maxFileSize) {
      alert(`File size exceeds ${maxFileSize / (1024 * 1024)}MB limit.`);
      return false;
    }

    return true;
  };

  const parseCSV = async (file: File): Promise<CSVPreviewData> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim() !== '');
          
          if (lines.length === 0) {
            reject(new Error('File CSV kosong'));
            return;
          }
          
          // Parse headers
          const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
          
          // Parse rows (limit to maxPreviewRows)
          const rows = lines.slice(1, maxPreviewRows + 1).map(line =>
            line.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''))
          );
          
          resolve({
            headers,
            rows,
            totalRows: lines.length - 1 // Exclude header
          });
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Gagal membaca file'));
      reader.readAsText(file);
    });
  };

  const handleFileProcess = async (file: File) => {
    setSelectedFile(file);
    onFileSelect(file);
    
    if (showPreview && file.name.endsWith('.csv')) {
      setIsLoadingPreview(true);
      try {
        const preview = await parseCSV(file);
        setPreviewData(preview);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        alert('Gagal membaca preview file CSV');
      } finally {
        setIsLoadingPreview(false);
      }
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (validateFile(file)) {
          handleFileProcess(file);
        }
      }
    },
    [onFileSelect, maxFileSize, acceptedFileTypes, showPreview]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (validateFile(file)) {
          handleFileProcess(file);
        }
      }
      // Reset input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onFileSelect, maxFileSize, acceptedFileTypes, showPreview]
  );

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewData(null);
    if (onFileRemove) {
      onFileRemove();
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleClick();
  };

  return (
    <>
      {!selectedFile ? (
        <FileUploaderContainer
          className={className}
          isDragging={isDragging}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <UploadIconWrapper>
            <UploadSVG />
          </UploadIconWrapper>
          <UploadText>
            Drag atau{' '}
            <span className="link" onClick={handleLinkClick}>
              pilih file
            </span>{' '}
            yang akan di import
          </UploadText>
          <FileTypeText>{acceptedFileTypes} file</FileTypeText>
          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept={acceptedFileTypes}
            onChange={handleFileInputChange}
          />
        </FileUploaderContainer>
      ) : (
        <PreviewContainer>
          <PreviewHeader>
            <PreviewHeaderLeft>
              <FileIcon>
                <DescriptionIcon />
              </FileIcon>
              <FileInfo>
                <FileName>{selectedFile.name}</FileName>
                <FileSize>{(selectedFile.size / 1024).toFixed(2)} KB</FileSize>
              </FileInfo>
            </PreviewHeaderLeft>
            <IconButton size="small" onClick={handleRemoveFile} style={{ 
              pointerEvents: isUploading ? 'none' : 'auto'
            }}>
              {isUploading ? <CircularProgress size={20} /> : <CloseIcon />}
            </IconButton>
          </PreviewHeader>
          
          {/* {isLoadingPreview ? (
            <EmptyPreview>Memuat preview...</EmptyPreview>
          ) : previewData ? (
            <>
              <PreviewTableWrapper>
                <PreviewTable>
                  <thead>
                    <tr>
                      {previewData.headers.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </PreviewTable>
              </PreviewTableWrapper>
              <PreviewFooter>
                Menampilkan {previewData.rows.length} dari {previewData.totalRows} baris data
              </PreviewFooter>
            </>
          ) : (
            <EmptyPreview>Preview tidak tersedia untuk file ini</EmptyPreview>
          )} */}
        </PreviewContainer>
      )}
    </>
  );
};

export default FileUploader;